import React, { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Wrench } from "lucide-react";
import { toolkit } from "../../content/siteContent";
import { SectionHeading } from "./primitives";
import { Reveal } from "../motion/Reveal";

const cellColor = (i, score, mode) => {
  if (i >= score) return "bg-white/10";
  if (mode === "after") return "bg-teal";
  return score <= 2 ? "bg-[#E8654F]" : "bg-gold";
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Heatmap = () => {
  const reduce = prefersReducedMotion();
  const [mode, setMode] = useState(reduce ? "after" : "before");
  const userSet = useRef(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const { heatmap } = toolkit;

  useEffect(() => {
    if (reduce) return; // reduced motion: already showing "after", no auto-animation
    if (inView && !userSet.current) {
      const t = setTimeout(() => {
        if (!userSet.current) setMode("after");
      }, 900);
      return () => clearTimeout(t);
    }
  }, [inView, reduce]);

  const handleToggle = (key) => {
    userSet.current = true;
    setMode(key);
  };

  return (
    <div
      ref={ref}
      data-testid="maturity-heatmap"
      className="border border-hair rounded-sm bg-navy p-6 lg:p-8 relative overflow-hidden"
    >
      <div className="absolute inset-0 blueprint opacity-40" />
      <div className="relative">
        <p className="eyebrow text-teal">{heatmap.title}</p>
        <p className="mt-1 text-white/50 text-xs">{heatmap.note}</p>

        <div className="mt-5 inline-flex rounded-sm border border-hair-dark p-1" role="tablist">
          {[
            { key: "before", label: "Before" },
            { key: "after", label: "After the program" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`heatmap-toggle-${t.key}`}
              onClick={() => handleToggle(t.key)}
              className={`font-display font-semibold text-xs px-4 py-2 rounded-sm transition-colors duration-300 ${
                mode === t.key ? "bg-teal text-white" : "text-white/60 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {heatmap.dimensions.map((d) => {
            const score = mode === "before" ? d.before : d.after;
            return (
              <div key={d.label} data-testid="heatmap-row">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/80 text-sm">{d.label}</span>
                  <motion.span
                    key={score}
                    initial={reduce ? false : { opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reduce ? 0 : 0.25 }}
                    className={`font-display font-bold text-sm ${mode === "after" ? "text-teal" : "text-gold"}`}
                  >
                    {score}
                  </motion.span>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={`h-2.5 rounded-sm ${reduce ? "" : "transition-colors duration-500"} ${cellColor(i, score, mode)}`}
                      style={reduce ? undefined : { transitionDelay: `${i * 70}ms` }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-white/60 text-xs leading-relaxed">{heatmap.caption}</p>
        <p className="mt-3 text-white/40 text-xs font-display tracking-wide uppercase">{heatmap.scale}</p>
      </div>
    </div>
  );
};

export const Toolkit = () => (
  <section
    id="tools"
    data-testid="toolkit-section"
    className="bg-white py-24 lg:py-32 border-t border-hair"
  >
    <div className="container-page">
      <SectionHeading eyebrow={toolkit.eyebrow}>{toolkit.heading}</SectionHeading>
      <Reveal delay={0.1}>
        <p className="mt-6 text-slate-ink text-base lg:text-lg leading-relaxed max-w-2xl">
          {toolkit.intro}
        </p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            {toolkit.tools.map((t, i) => (
              <Reveal
                key={t.title}
                delay={(i % 2) * 0.05}
                className="flex items-start gap-3 border-b border-hair pb-5"
              >
                <Wrench className="h-4 w-4 text-teal mt-1 shrink-0" />
                <div>
                  <p className="font-display font-semibold text-navy text-[15px] tracking-tight">{t.title}</p>
                  <p className="mt-1 text-slate-ink text-sm leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <Heatmap />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
